import { useApp, useExcalidrawAPI } from "@excalidraw/excalidraw";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchTemplateScene,
  fetchTemplates,
  setTemplateFavorite,
  type TemplateItem,
  type TemplateScope,
} from "../dashboard/lib/api";

import "./LibraryTemplates.scss";

/**
 * The library panel's "Templates" tab (mounted via the editor's
 * `renderLibraryTemplates` prop, so this file owns everything the editor
 * package can't reach: Supabase auth, the /api/templates catalog, and
 * stamping a template's scene onto the board that's already open).
 *
 * Deliberately distinct from the dashboard's "Use template", which creates a
 * whole new board and counts against the plan's board cap. Here the user is
 * already in a board they own, so this inserts into it and creates nothing —
 * see the GET /templates/:id/scene comment in workers/api.
 */

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

/** Custom drag payload. The scene is fetched on drag start (it's far too big
 *  for dataTransfer), so only the id travels; the drop handler joins it back
 *  up with the in-flight fetch below. */
const TEMPLATE_MIME = "application/vnd.chartoon-template-id";

const HeartGlyph = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// The favorites tab is icon-only — five text labels don't fit the popover.
const SCOPES: { scope: TemplateScope; label: string; iconOnly?: boolean }[] = [
  { scope: "all", label: "All" },
  { scope: "global", label: "Chartoon" },
  { scope: "community", label: "Community" },
  { scope: "mine", label: "Mine" },
  { scope: "favorites", label: "Favorites", iconOnly: true },
];

const PlaceholderGlyph = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M9 9v11" strokeLinecap="round" />
  </svg>
);

/** Raw fetch failures read like stack traces ("Failed to fetch") — say what
 *  actually happened in words a user can act on. */
const friendlyError = (err: unknown): string => {
  const message = err instanceof Error ? err.message : "";
  if (err instanceof TypeError || /failed to fetch/i.test(message)) {
    return "Can't reach Chartoon right now. Check your connection and try again.";
  }
  return message || "Couldn't load templates.";
};

export const LibraryTemplates: React.FC = () => {
  const app = useApp();
  // toasts live on the imperative API, not on AppClassProperties
  const excalidrawAPI = useExcalidrawAPI();

  const [scope, setScope] = useState<TemplateScope>("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insertingId, setInsertingId] = useState<string | null>(null);
  // bumping this refetches the current page — "Try again", heart-revert
  const [refreshTick, setRefreshTick] = useState(0);

  // debounce typing so each keystroke isn't a request
  useEffect(() => {
    const timer = window.setTimeout(
      () => setQuery(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [search]);

  // scope/query change resets to the first page
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchTemplates({ page: 1, pageSize: PAGE_SIZE, q: query, scope })
      .then((list) => {
        if (cancelled) {
          return;
        }
        setItems(list.templates);
        setTotal(list.total);
        setPage(1);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(friendlyError(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [scope, query, refreshTick]);

  const loadMore = useCallback(() => {
    const next = page + 1;
    setLoadingMore(true);
    fetchTemplates({ page: next, pageSize: PAGE_SIZE, q: query, scope })
      .then((list) => {
        // concat rather than replace, and drop any id already shown — paging
        // by OFFSET can repeat a row if one is inserted mid-scroll
        setItems((prev) => {
          const seen = new Set(prev.map((t) => t.id));
          return [...prev, ...list.templates.filter((t) => !seen.has(t.id))];
        });
        setTotal(list.total);
        setPage(next);
      })
      .catch((err) => setError(friendlyError(err)))
      .finally(() => setLoadingMore(false));
  }, [page, query, scope]);

  /** Optimistic heart flip; a failure refetches so the UI never lies. */
  const toggleFavorite = useCallback(
    async (template: TemplateItem, event: React.MouseEvent) => {
      // the card behind the heart inserts on click — keep those separate
      event.stopPropagation();
      const next = !template.isFavorite;
      setItems((prev) =>
        scope === "favorites" && !next
          ? prev.filter((t) => t.id !== template.id)
          : prev.map((t) =>
              t.id === template.id ? { ...t, isFavorite: next } : t,
            ),
      );
      if (scope === "favorites" && !next) {
        setTotal((t) => Math.max(0, t - 1));
      }
      try {
        await setTemplateFavorite(template.id, next);
      } catch (err) {
        setRefreshTick((t) => t + 1);
        excalidrawAPI?.setToast({
          message: friendlyError(err),
          closable: true,
        });
      }
    },
    [scope, excalidrawAPI],
  );

  /** Stamp a template's elements into the open board. `position` is a client
   *  point for drops, or "center" for clicks. */
  const insert = useCallback(
    async (
      template: TemplateItem,
      position: { clientX: number; clientY: number } | "center",
      prefetched?: Promise<{ scene: { elements: any; files?: any } }>,
    ) => {
      setInsertingId(template.id);
      try {
        const { scene } = await (prefetched ?? fetchTemplateScene(template.id));
        if (!scene?.elements?.length) {
          excalidrawAPI?.setToast({
            message: "That template is empty.",
            closable: true,
          });
          return;
        }
        // files must ride along with the elements or images render blank —
        // onInsertElements passes files:null, so go through the real entry
        // point. Elements get fresh ids/seeds inside.
        app.addElementsFromPasteOrLibrary({
          elements: scene.elements,
          files: scene.files ?? null,
          position,
        });
      } catch (err: any) {
        excalidrawAPI?.setToast({
          message: err?.message || "Couldn't insert that template.",
          closable: true,
        });
      } finally {
        setInsertingId(null);
      }
    },
    [app, excalidrawAPI],
  );

  // --- drag & drop -------------------------------------------------------
  // The editor's own drop handler only knows about library items and files,
  // so a capture-phase listener claims our MIME first and stops it there.
  // Scene fetch starts on dragstart so the drop feels immediate.
  const dragged = useRef<{
    template: TemplateItem;
    scene: Promise<{ scene: { elements: any; files?: any } }>;
  } | null>(null);

  useEffect(() => {
    const onDrop = (event: DragEvent) => {
      const pending = dragged.current;
      if (!pending || !event.dataTransfer?.types.includes(TEMPLATE_MIME)) {
        return;
      }
      dragged.current = null;
      event.preventDefault();
      event.stopPropagation();
      void insert(
        pending.template,
        { clientX: event.clientX, clientY: event.clientY },
        pending.scene,
      );
    };
    // dragover must be prevented too, else the browser never fires drop
    const onDragOver = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes(TEMPLATE_MIME)) {
        event.preventDefault();
      }
    };
    document.addEventListener("drop", onDrop, true);
    document.addEventListener("dragover", onDragOver, true);
    return () => {
      document.removeEventListener("drop", onDrop, true);
      document.removeEventListener("dragover", onDragOver, true);
    };
  }, [insert]);

  const onDragStart = (template: TemplateItem, event: React.DragEvent) => {
    event.dataTransfer.setData(TEMPLATE_MIME, template.id);
    event.dataTransfer.effectAllowed = "copy";
    // start the download now; the drop awaits this same promise. Swallow here
    // so an early rejection isn't an unhandled rejection — `insert` awaits it
    // again and reports the failure to the user.
    const scene = fetchTemplateScene(template.id);
    scene.catch(() => {});
    dragged.current = { template, scene };
  };

  const hasMore = items.length < total;

  return (
    <div className="library-templates">
      <div
        className="library-templates__scopes hig-segmented"
        role="tablist"
        aria-label="Template source"
      >
        {SCOPES.map((entry) => (
          <button
            key={entry.scope}
            type="button"
            role="tab"
            aria-selected={scope === entry.scope}
            aria-label={entry.label}
            title={entry.iconOnly ? entry.label : undefined}
            className={`library-templates__scope hig-segmented__item${
              entry.iconOnly ? " library-templates__scope--icon" : ""
            }`}
            onClick={() => setScope(entry.scope)}
          >
            {entry.iconOnly ? (
              <HeartGlyph filled={scope === entry.scope} />
            ) : (
              entry.label
            )}
          </button>
        ))}
      </div>

      <input
        className="library-templates__search"
        type="search"
        placeholder="Search templates"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <div className="library-templates__body">
        {loading ? (
          <div className="library-templates__grid" aria-hidden="true">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="library-templates__card library-templates__card--skeleton"
              >
                <span className="library-templates__thumb" />
                <span className="library-templates__skeleton-line" />
                <span className="library-templates__skeleton-line library-templates__skeleton-line--short" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="library-templates__status">
            <div>{error}</div>
            <button
              type="button"
              className="library-templates__retry"
              onClick={() => setRefreshTick((t) => t + 1)}
            >
              Try again
            </button>
          </div>
        ) : !items.length ? (
          <div className="library-templates__status">
            {query
              ? `No templates match “${query}”.`
              : scope === "mine"
              ? "You haven't saved any templates yet."
              : scope === "favorites"
              ? "Nothing saved yet — tap the heart on any template to keep it here."
              : "No templates here yet."}
          </div>
        ) : (
          <>
            <div className="library-templates__grid">
              {/* a real <button> can't nest the heart button, so the card is a
                  div with button semantics */}
              {items.map((template) => (
                <div
                  key={template.id}
                  role="button"
                  tabIndex={0}
                  className={`library-templates__card${
                    insertingId === template.id
                      ? " library-templates__card--busy"
                      : ""
                  }`}
                  draggable
                  onDragStart={(event) => onDragStart(template, event)}
                  onClick={() => {
                    if (!insertingId) {
                      void insert(template, "center");
                    }
                  }}
                  onKeyDown={(event) => {
                    if (
                      (event.key === "Enter" || event.key === " ") &&
                      !insertingId
                    ) {
                      event.preventDefault();
                      void insert(template, "center");
                    }
                  }}
                  aria-disabled={insertingId === template.id}
                  title={`${template.name}${
                    template.description ? `\n${template.description}` : ""
                  }\nClick to insert, or drag onto the canvas`}
                >
                  <span className="library-templates__thumb">
                    {template.thumbnailUrl ? (
                      <img
                        src={template.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        draggable={false}
                      />
                    ) : (
                      <span className="library-templates__thumb-empty">
                        {PlaceholderGlyph}
                      </span>
                    )}
                    <button
                      type="button"
                      className={`library-templates__fav${
                        template.isFavorite
                          ? " library-templates__fav--active"
                          : ""
                      }`}
                      onClick={(event) => void toggleFavorite(template, event)}
                      aria-pressed={template.isFavorite}
                      aria-label={
                        template.isFavorite
                          ? "Remove from favorites"
                          : "Add to favorites"
                      }
                      title={
                        template.isFavorite
                          ? "Remove from favorites"
                          : "Add to favorites"
                      }
                    >
                      <HeartGlyph filled={template.isFavorite} />
                    </button>
                  </span>
                  <span className="library-templates__name">
                    {template.name}
                  </span>
                  <span className="library-templates__author">
                    {template.isGlobal
                      ? "By Chartoon"
                      : template.isMine
                      ? "You"
                      : template.authorName}
                  </span>
                </div>
              ))}
            </div>
            {hasMore && (
              <button
                type="button"
                className="library-templates__more"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
